<script setup lang="ts">
import { ref, computed } from 'vue'
import type { Project } from '@/composables/useApi'

// Props
interface Props {
  projects: Project[]
  loading: boolean
  error: string | null
  title?: string
  showSearch?: boolean
  showFilters?: boolean
  showRefresh?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  title: 'Project Details',
  showSearch: true,
  showFilters: true,
  showRefresh: true
})

// Events
const emit = defineEmits<{
  refresh: []
  viewProject: [project: Project]
  editProject: [project: Project]
  sellSODAs: [project: Project]
  createProject: []
  search: [query: string]
  filterStatus: [status: string]
}>()

// Local state for search and filters
const searchQuery = ref('')
const statusFilter = ref('All Status')

// Status filter options
const statusOptions = [
  'All Status',
  'Active',
  'Pending',
  'Completed',
  'Draft'
]

// Filtered projects based on search and status
const filteredProjects = computed(() => {
  let filtered = props.projects

  // Apply search filter
  if (searchQuery.value.trim()) {
    const query = searchQuery.value.toLowerCase()
    filtered = filtered.filter(project =>
      project.projectName?.toLowerCase().includes(query) ||
      project.methodologyName?.toLowerCase().includes(query) ||
      project.projectId.toLowerCase().includes(query)
    )
  }

  // Apply status filter
  if (statusFilter.value !== 'All Status') {
    filtered = filtered.filter(project => 
      project.status?.toLowerCase() === statusFilter.value.toLowerCase()
    )
  }

  return filtered
})

// Helper functions
const getStatusColor = (status: string | null | undefined) => {
  switch (status?.toLowerCase()) {
    case 'active':
      return 'bg-green-100 text-green-800'
    case 'pending':
      return 'bg-yellow-100 text-yellow-800'
    case 'completed':
      return 'bg-blue-100 text-blue-800'
    case 'draft':
      return 'bg-gray-100 text-gray-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

const formatDate = (dateString: string | undefined) => {
  if (!dateString) return 'N/A'
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

// Event handlers
const handleRefresh = () => {
  emit('refresh')
}

const handleViewProject = (project: Project) => {
  emit('viewProject', project)
}

const handleEditProject = (project: Project) => {
  emit('editProject', project)
}

const handleSellSODAs = (project: Project) => {
  emit('sellSODAs', project)
}

const handleCreateProject = () => {
  emit('createProject')
}

const handleSearch = () => {
  emit('search', searchQuery.value)
}

const handleFilterChange = () => {
  emit('filterStatus', statusFilter.value)
}
</script>

<template>
  <section class="bg-white rounded-lg shadow-sm border">
    <!-- Table Header -->
    <div class="p-6 border-b">
      <div class="flex justify-between items-center">
        <h2 class="text-xl font-semibold text-gray-900">{{ title }}</h2>
        <div class="flex space-x-3">
          <!-- Search Input -->
          <input 
            v-if="showSearch"
            v-model="searchQuery"
            @input="handleSearch"
            type="text" 
            placeholder="Search projects..." 
            class="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
          
          <!-- Status Filter -->
          <select 
            v-if="showFilters"
            v-model="statusFilter"
            @change="handleFilterChange"
            class="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option v-for="status in statusOptions" :key="status" :value="status">
              {{ status }}
            </option>
          </select>
          
          <!-- Refresh Button -->
          <button 
            v-if="showRefresh"
            @click="handleRefresh" 
            :disabled="loading"
            class="bg-blue-600 text-white px-3 py-2 rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            <i class="fa-solid fa-refresh mr-1" :class="{ 'fa-spin': loading }"></i>
            Refresh
          </button>
        </div>
      </div>
    </div>
    
    <!-- Loading State -->
    <div v-if="loading" class="p-8 text-center">
      <i class="fa-solid fa-spinner fa-spin text-2xl text-blue-600 mb-4"></i>
      <p class="text-gray-600">Loading projects...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="p-8 text-center">
      <i class="fa-solid fa-exclamation-triangle text-2xl text-red-600 mb-4"></i>
      <p class="text-red-600 mb-4">{{ error }}</p>
      <button 
        @click="handleRefresh" 
        class="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
      >
        Try Again
      </button>
    </div>

    <!-- Empty State -->
    <div v-else-if="filteredProjects.length === 0 && projects.length === 0" class="p-8 text-center">
      <i class="fa-solid fa-folder-open text-4xl text-gray-400 mb-4"></i>
      <p class="text-gray-600 mb-4">No projects found</p>
      <button 
        @click="handleCreateProject"
        class="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
      >
        Create Your First Project
      </button>
    </div>

    <!-- No Results State (when filtering) -->
    <div v-else-if="filteredProjects.length === 0" class="p-8 text-center">
      <i class="fa-solid fa-search text-4xl text-gray-400 mb-4"></i>
      <p class="text-gray-600 mb-4">No projects match your search criteria</p>
      <button 
        @click="searchQuery = ''; statusFilter = 'All Status'"
        class="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
      >
        Clear Filters
      </button>
    </div>

    <!-- Projects Table -->
    <div v-else class="overflow-x-auto">
      <table class="w-full">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Project Name
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Methodology
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              SODAs Minted
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Created
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr 
            v-for="project in filteredProjects" 
            :key="project.projectId" 
            class="hover:bg-gray-50 transition-colors"
          >
            <!-- Project Name Column -->
            <td class="px-6 py-4 whitespace-nowrap">
              <div class="text-sm font-medium text-gray-900">
                {{ project.projectName || 'Untitled Project' }}
              </div>
              <div class="text-sm text-gray-500">ID: {{ project.projectId }}</div>
              <div
                v-if="project.jurisdiction"
                class="text-xs text-gray-400 mt-1 max-w-xs truncate"
              >
                {{ project.jurisdiction }}
              </div>
            </td>
            
            <!-- Methodology Column -->
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
              {{ project.methodologyName || 'N/A' }}
            </td>
            
            <!-- Status Column -->
            <td class="px-6 py-4 whitespace-nowrap">
              <span 
                :class="getStatusColor(project.status)" 
                class="px-2 py-1 text-xs font-medium rounded-full"
              >
                {{ project.status || 'Unknown' }}
              </span>
            </td>
            
            <!-- SODAs Minted Column -->
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
              {{ project.assetsMinted?.toLocaleString() || '0' }}
            </td>
            
            <!-- Created Date Column -->
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
              {{ formatDate(project.created_at) }}
            </td>
            
            <!-- Actions Column -->
            <td class="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
              <button 
                @click="handleViewProject(project)"
                class="text-blue-600 hover:text-blue-900 transition-colors"
                title="View Project"
              >
                <i class="fa-solid fa-eye"></i>
              </button>
              <button 
                @click="handleEditProject(project)"
                class="text-green-600 hover:text-green-900 transition-colors"
                title="Edit Project"
              >
                <i class="fa-solid fa-edit"></i>
              </button>
              <button 
                @click="handleSellSODAs(project)"
                :disabled="!project.assetsMinted || project.assetsMinted === 0"
                :class="project.assetsMinted && project.assetsMinted > 0 
                  ? 'text-purple-600 hover:text-purple-900' 
                  : 'text-gray-400 cursor-not-allowed'"
                :title="project.assetsMinted && project.assetsMinted > 0 ? 'Sell SODAs' : 'No SODAs available'"
                class="transition-colors"
              >
                <i class="fa-solid fa-coins"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Results Summary -->
    <div v-if="!loading && !error && projects.length > 0" class="px-6 py-3 bg-gray-50 border-t text-sm text-gray-600">
      Showing {{ filteredProjects.length }} of {{ projects.length }} projects
      <span v-if="searchQuery || statusFilter !== 'All Status'" class="ml-2">
        (filtered)
      </span>
    </div>
  </section>
</template>

<style scoped>
/* Component-specific styles if needed */
</style>